import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import OrderService from '../../../services/order.service';

function OrderDetails({ order, open, onClose }) {
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [transactionId, setTransactionId] = useState(order?.payment?.idTransaction || '');
  const [paymentPartner, setPaymentPartner] = useState("FEDAPAY");
  const [refundItems, setRefundItems] = useState([]);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(order?.client?.email || '');
  const [regenerating, setRegenerating] = useState(false);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceDetails, setForceDetails] = useState(null);
  const [forceOptions, setForceOptions] = useState({ force_pdf: false, force_dgi: false });
  const [refundInvoiceUrl, setRefundInvoiceUrl] = useState(order?.refund_invoice?.url || null);

  useEffect(() => {
    setNewEmail(order?.client?.email || '');
    setRefundInvoiceUrl(order?.refund_invoice?.url || null);
  }, [order]);

  const handleVerifyPayment = async () => {
    if (!transactionId) {
      setVerificationResult({
        success: false,
        message: "Veuillez entrer l'ID de transaction."
      });
      return;
    }

    setVerifying(true);
    setVerificationResult(null);
    
    try {
      const payload = {
        order_id: order?.id,
        id_transaction: transactionId,
        payment_partner: paymentPartner
      };
      
      const response = await OrderService.verifyPayment(payload);
      setVerificationResult({
        success: response.statut,
        message: response.success ? "Vérification effectuée avec succès" : "Échec de la vérification"
      });
    } catch (error) {
      setVerificationResult({
        success: false,
        message: error.message || "Erreur lors de la vérification"
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleRefund = async () => {
    if (refundItems.length === 0) {
      setVerificationResult({ success: false, message: "Veuillez sélectionner au moins un article à rembourser." });
      return;
    }

    const payload = {
      order_id: order?.id,
      order_items: refundItems
    };

    try {
      const response = await OrderService.refundOrder(payload);
      if (response?.refund_invoice_url) {
        setRefundInvoiceUrl(response.refund_invoice_url);
      }
      setShowRefundDialog(false);
      setVerificationResult({ success: true, message: response?.message || "Remboursement effectué avec succès." });
    } catch (error) {
      setVerificationResult({ success: false, message: error?.message || "Erreur lors du remboursement." });
    }
  };

  const handleRegenerateInvoice = async (options = {}) => {
    if (!order?.number) {
      setVerificationResult({ success: false, message: "Référence de commande introuvable." });
      return;
    }
    setRegenerating(true);
    setVerificationResult(null);
    try {
      const response = await OrderService.regenerateInvoice(order.number, options);
      setVerificationResult({ success: true, message: response?.message || "Facture régénérée avec succès." });
      setForceDialogOpen(false);
    } catch (error) {
      const hasForceDetails = error?.invoice_record_exists !== undefined
        || error?.dgi_code_exists !== undefined
        || error?.pdf_exists !== undefined;
      if (hasForceDetails) {
        setForceDetails(error);
        setForceOptions({ force_pdf: false, force_dgi: false });
        setForceDialogOpen(true);
      } else {
        setVerificationResult({ success: false, message: error?.message || "Erreur lors de la régénération de la facture." });
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleResendOrderEmail = async () => {
    if (!order?.id) {
      setVerificationResult({
        success: false,
        message: "Identifiant de commande introuvable.",
      });
      return;
    }
    if (!newEmail || !newEmail.includes('@')) {
      setVerificationResult({
        success: false,
        message: "Veuillez saisir une adresse email valide.",
      });
      return;
    }

    setResendingEmail(true);
    try {
      const response = await OrderService.resendOrderEmail(order.id, newEmail);
      setVerificationResult({
        success: true,
        message: response?.message || "Mail de commande renvoyé avec succès.",
      });
    } catch (error) {
      setVerificationResult({
        success: false,
        message: error?.message || "Erreur lors de l'envoi du mail de commande.",
      });
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>Détails de la commande {order?.number}</Typography>
          {order?.order_status === 'pending' && (
            <>
              {order?.payment?.idTransaction ? (
                <Button
                  variant="outlined"
                  startIcon={verifying ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleVerifyPayment}
                  disabled={verifying}
                  sx={{ ml: 2 }}
                >
                  Revérifier paiement
                </Button>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Entrez l'ID de transaction"
                    style={{ marginRight: '8px' }}
                  />
                  <Select
                    value={paymentPartner}
                    onChange={(e) => setPaymentPartner(e.target.value)}
                    sx={{ marginRight: '8px' }}
                  >
                    <MenuItem value="FEDAPAY">FEDAPAY</MenuItem>
                    <MenuItem value="KKIAPAY">KKIAPAY</MenuItem>
                  </Select>
                  <Button
                    variant="outlined"
                    onClick={handleVerifyPayment}
                    disabled={verifying}
                    startIcon={<CheckIcon />}
                  >
                    Vérifier
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {verificationResult && (
          <Alert 
            severity={verificationResult.success ? "success" : "error"}
            sx={{ mb: 2 }}
          >
            {verificationResult.message}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Informations client</Typography>
              <Typography variant="body1">
                Nom: {order?.client?.firstName} {order?.client?.lastName}
              </Typography>
              <Typography variant="body1">Email: {order?.client?.email}</Typography>
              <Typography variant="body1">Téléphone: {order?.client?.phone}</Typography>
              <Typography variant="body1">Pays: {order?.client?.country}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Informations commande</Typography>
              <Typography variant="body1">
                Id: {order?.id}
              </Typography>
              <Typography variant="body1">
                Montant total: {Number(order?.amount).toLocaleString()} FCFA
              </Typography>
              <Typography variant="body1">
                Date: {new Date(order?.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body1">
                Statut: <Chip 
                  label={order?.order_status} 
                  color={order?.order_status === 'success' ? 'success' : 'error'}
                  size="small"
                />
              </Typography>
              <Typography variant="body1">
                Statut retrait: <Chip 
                  label={order?.status_withdraw} 
                  color={order?.status_withdraw === 'success' ? 'success' : 'warning'}
                  size="small"
                />
              </Typography>
              <Typography variant="body1">
                Date de retrait: <Chip 
                  label={order?.date_withdraw?.date} 
                  size="small"
                />
              </Typography>
            </Grid>

            {order?.payment && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Paiement</Typography>
                <Typography variant="body1">
                  Méthode: {order?.payment?.paymentMethod} ({order?.payment?.operator})
                </Typography>
                <Typography variant="body1">
                  Transaction ID: {order?.payment?.idTransaction}
                </Typography>
                <Typography variant="body1">
                  Montant: {Number(order?.payment?.montant).toLocaleString()} {order?.payment?.currency}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Articles commandés</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Prix unitaire</TableCell>
                      <TableCell align="right">Montant</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order?.orderItems?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{Number(item.price).toLocaleString()} FCFA</TableCell>
                        <TableCell align="right">{Number(item.amount).toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {order?.orderedTickets && order.orderedTickets.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Tickets</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Activités</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Numéro</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.orderedTickets.map((ticket, index) => (
                        <TableRow key={index}>
                          <TableCell>{ticket.name}</TableCell>
                          <TableCell>{ticket.activities.join(', ')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={ticket.statut}
                              color={ticket.statut === 'valid' ? 'success' : 'warning'}
                              size="small"
                            />
                            {ticket.dateVerif && (
                              <Typography variant="caption" display="block">
                                {new Date(ticket.dateVerif).toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{ticket.number}</TableCell>
                          <TableCell>
                            <Button 
                              variant="outlined" 
                              size="small"
                              href={ticket.url}
                              target="_blank"
                            >
                              Voir le ticket
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Facture</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {order?.invoice?.url && (
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    href={order.invoice.url}
                    target="_blank"
                  >
                    Télécharger la facture
                  </Button>
                )}
                {refundInvoiceUrl && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<DownloadIcon />}
                    href={refundInvoiceUrl}
                    target="_blank"
                  >
                    Facture de remboursement
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  onClick={() => { setForceDetails(null); setForceOptions({ force_pdf: false, force_dgi: false }); setForceDialogOpen(true); }}
                >
                  Régénérer la facture
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {order?.order_status === 'success' && (
          <Button
            variant="outlined"
            onClick={() => setShowRefundDialog(true)}
            sx={{ mt: 2 }}
          >
            Procéder au remboursement
          </Button>
        )}
        {showRefundDialog && (
          <Dialog open={showRefundDialog} onClose={() => setShowRefundDialog(false)}>
            <DialogTitle>Sélectionner les articles à rembourser</DialogTitle>
            <DialogContent>
              {order?.orderItems?.map((item, index) => (
                <div key={index}>
                  <Checkbox
                    checked={refundItems.includes(item.id)}
                    onChange={() => {
                      setRefundItems((prev) =>
                        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                      );
                    }}
                  />
                  {item.name}
                </div>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowRefundDialog(false)}>Annuler</Button>
              <Button onClick={handleRefund}>Confirmer le remboursement</Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog open={forceDialogOpen} onClose={() => setForceDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Options de régénération</DialogTitle>
          <DialogContent>
            {forceDetails && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>{forceDetails.message}</Alert>
                <Typography variant="subtitle2" gutterBottom>État actuel</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Enregistrement facture</Typography>
                    <Chip size="small" label={forceDetails.invoice_record_exists ? 'Existe' : 'Absent'} color={forceDetails.invoice_record_exists ? 'success' : 'error'} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Code DGI</Typography>
                    <Chip size="small" label={forceDetails.dgi_code_exists ? 'Existe' : 'Absent'} color={forceDetails.dgi_code_exists ? 'success' : 'error'} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Fichier PDF</Typography>
                    <Chip size="small" label={forceDetails.pdf_exists ? 'Existe' : 'Absent'} color={forceDetails.pdf_exists ? 'success' : 'error'} />
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </>
            )}
            <Typography variant="subtitle2" gutterBottom>Forcer</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceOptions.force_dgi}
                    onChange={(e) => setForceOptions(prev => ({ ...prev, force_dgi: e.target.checked }))}
                  />
                }
                label="Créer / régénérer le code DGI (force_dgi)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceOptions.force_pdf}
                    onChange={(e) => setForceOptions(prev => ({ ...prev, force_pdf: e.target.checked }))}
                  />
                }
                label="Régénérer le fichier PDF (force_pdf)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForceDialogOpen(false)}>Annuler</Button>
            <Button
              variant="contained"
              startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={() => handleRegenerateInvoice(forceOptions)}
              disabled={regenerating}
            >
              {regenerating ? 'Relance...' : 'Relancer'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <TextField
          size="small"
          type="email"
          label="Email de renvoi"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          sx={{ minWidth: 280, mr: 'auto' }}
        />
        <Button
          variant="contained"
          startIcon={resendingEmail ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
          onClick={handleResendOrderEmail}
          disabled={resendingEmail}
        >
          {resendingEmail ? 'Envoi...' : 'Renvoyer le mail'}
        </Button>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderDetails; 